import React, { useEffect, useState, useContext } from "react";
import H5 from "components/typography/H5";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge, Button } from "theme-ui";
import { themevals } from "theme/themevals";
import FileUpload from "./FileUpload";
import moment from "moment-timezone";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import { TrashIcon, QuestionMarkIcon, EllipsisIcon } from "components/svg";
import BasicModal from "components/ui/BasicModal";
import BasicButton from "components/forms/BasicButton";
import P from "components/typography/P";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { getProjectPurchaseOrders } from "api/PurchaseOrder";
import { useNavigate } from "react-router-dom";

const PurchaseOrders = ({ isCompany, project, isProvider }: { isCompany: boolean; project: any; isProvider: boolean }) => {
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  const columnHelper = createColumnHelper();
  const navigate = useNavigate();

  useEffect(() => {
    _init();
  }, []);

  const _init = async () => {
    const res = await getProjectPurchaseOrders({ data: project });
    if (res?.length) {
      setPurchaseOrders(res);
    } else {
      setPurchaseOrders([]);
    }
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.sequence, {
      id: "id",
      cell: (info: any) => {
        const row = info.row.original;
        return (
          <Box sx={{ fontWeight: 600, cursor: "pointer", color: "#000" }} onClick={() => navigate(`/purchase-orders/${info.row.original.id}`)}>
            {`GMP-${String(info.getValue()).padStart(5, "0")}`}
          </Box>
        );
      },
      header: () => <span>PO#</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => {
        switch (info.getValue()) {
          case 0:
            return (
              <Badge as="span" variant="warning">
                New
              </Badge>
            );
          case 1:
            return (
              <Badge as="span" variant="info">
                Sent
              </Badge>
            );
          case 2:
            return (
              <Badge as="span" variant="success">
                Accepted
              </Badge>
            );
          case 3:
            return (
              <Badge as="span" variant="warning">
                Billed
              </Badge>
            );
          case 4:
            return (
              <Badge as="span" variant="danger">
                Denied
              </Badge>
            );
          case 5:
            return (
              <Badge as="span" variant="success">
                Closed
              </Badge>
            );
          case 6:
            return (
              <Badge as="span" variant="danger">
                Cancelled
              </Badge>
            );
          default:
            return (
              <Badge as="span" variant="warning">
                New
              </Badge>
            );
        }
      },
      header: () => <span>Status</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.paymentStatus, {
      id: "paymentStatus",
      cell: (info: any) => {
        switch (info.getValue()) {
          case 0:
            return (
              <Badge as="span" variant="orange">
                Open
              </Badge>
            );
          case 1:
            return (
              <Badge as="span" variant="info">
                Due
              </Badge>
            );
          case 2:
            return (
              <Badge as="span" variant="success">
                Paid
              </Badge>
            );
          case 3:
            return (
              <Badge as="span" variant="danger">
                Cancelled
              </Badge>
            );
          default:
            return (
              <Badge as="span" variant="orange">
                Open
              </Badge>
            );
        }
      },
      header: () => <span>Payment Status</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.lineitems, {
      id: "bulkProductQty",
      cell: (info: any) => {
        const lineitems = info.getValue() || [];
        const productItem = lineitems.find((item: any) => item.type === "product");

        if (productItem) {
          return `${productItem.quantity} ${productItem.unit || ""}`.trim();
        }
        return "-";
      },
      header: () => <span>Bulk Product Qty</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.lineitems, {
      id: "addOns",
      cell: (info: any) => {
        const lineitems = info.getValue() || [];
        const addonItem = lineitems.find((item: any) => item.type === "addon-product");

        return addonItem ? "Yes" : "No";
      },
      header: () => <span>Add-Ons?</span>,
      meta: {
        style: { width: "100px", minWidth: "100px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.lineitems, {
      id: "total",
      cell: (info: any) => {
        const lineitems = info.getValue() || [];
        const total = lineitems.reduce((sum: number, item: any) => {
          const cost = parseFloat(item.cost) || 0;
          const quantity = parseFloat(item.quantity) || 0;
          return sum + cost * quantity;
        }, 0);

        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(total);
      },
      header: () => <span>Total</span>,
      meta: {
        style: { width: "120px", minWidth: "120px" },
      },
    }),
  ];

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Purchase Orders
      </H5>
      {purchaseOrders?.length > 0 && (
        <TableBasic data={purchaseOrders} columns={columns} enablePaging={false} enableSearch={false} customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }} />
      )}
    </>
  );
};

export default PurchaseOrders;
